import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import InnerPageHero from '@/components/feature/InnerPageHero';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';
import FadeInSection from '@/components/base/FadeInSection';

const businessTypes = [
  {
    icon: 'ri-motorbike-line',
    title: 'Quick Commerce',
    description: 'Reliable fresh produce supply for quick-commerce platforms needing consistent inventory for rapid delivery operations.',
  },
  {
    icon: 'ri-restaurant-line',
    title: 'Restaurants',
    description: 'Fresh ingredients delivered regularly for restaurants of all sizes, ensuring kitchen-ready quality every time.',
  },
  {
    icon: 'ri-hotel-line',
    title: 'Hotels',
    description: 'Premium quality fruits and vegetables for hotel kitchens, banquets, and room service operations.',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Cloud Kitchens',
    description: 'Consistent bulk supply for cloud kitchens and food delivery brands requiring reliable fresh ingredients.',
  },
  {
    icon: 'ri-shopping-bag-3-line',
    title: 'Retail Stores',
    description: 'Wholesale fresh produce for retail outlets, grocery stores, and neighborhood shops.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Supermarkets',
    description: 'Large-volume supply for supermarket chains with consistent quality and scheduled delivery.',
  },
  {
    icon: 'ri-archive-line',
    title: 'Wholesale Markets',
    description: 'Bulk produce supply for wholesale distributors and mandi traders across regions.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Food Businesses',
    description: 'Custom supply solutions for catering services, food processors, and packaged food manufacturers.',
  },
];

export default function BulkSupply() {
  const { openEnquiry } = useEnquiryModal();
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <InnerPageHero
          bgImage="/images/bulk-supply-hero.jpg"
          bgAlt="Bulk fresh produce supply warehouse"
          bgTitle="Bassi Enterprises - Bulk Fresh Produce Supply"
          breadcrumbLabel="Bulk Supply"
        >
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full text-xs font-semibold mb-5 border border-background-50/15">
              <i className="ri-store-2-line"></i>
              B2B Fresh Produce Supply
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              Bulk Fresh Produce Supply
            </h1>
            <p className="text-background-50/50 text-sm md:text-base font-semibold mb-4 tracking-wide uppercase">
              Your Reliable Fruits &amp; Vegetables Partner
            </p>
            <p className="text-background-200 text-base md:text-lg leading-relaxed mb-8">
              We provide consistent fresh produce supply solutions for businesses, retailers and food companies. From daily restaurant supply to bulk orders for chains — we deliver.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <button
                onClick={() => openEnquiry()}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-400 text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-fill text-xl"></i>
                Request Bulk Supply Quote
              </button>
              <a
                href="tel:+919084756574"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50/10 hover:bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/20 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill text-lg"></i>
                Call Now
              </a>
            </div>
          </div>
        </InnerPageHero>

        {/* Who We Serve */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <FadeInSection>
              <div className="text-center mb-12">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold mb-4">
                  <i className="ri-group-line"></i>
                  Who We Serve
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  We Supply Fresh Produce For
                </h2>
                <p className="text-foreground-500 text-sm md:text-base mt-3 max-w-xl mx-auto">
                  From startups to enterprises — our bulk supply solutions are tailored to fit your business needs.
                </p>
              </div>
            </FadeInSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {businessTypes.map((item, idx) => (
                <FadeInSection key={idx} delay={idx * 80}>
                  <div
                    className="group bg-background-50 border border-background-200 rounded-2xl p-6 md:p-7 hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-500 transition-colors duration-300">
                      <i className={`${item.icon} text-primary-600 group-hover:text-background-50 text-lg transition-colors duration-300`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-base text-foreground-950 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </section>

        {/* Bulk Supply Enquiry Form */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-background-100">
          <FadeInSection>
            <div className="max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-2xl border border-background-200 p-8 md:p-10">
                <div className="text-center mb-8">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold mb-4">
                    <i className="ri-send-plane-line"></i>
                    Get In Touch
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                    Request Bulk Supply Quote
                  </h2>
                  <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
                    Fill in the form below and our team will get back to you with competitive bulk pricing within 24 hours.
                  </p>
                </div>

                <form
                  data-readdy-form
                  action="https://readdy.ai/api/form/d8mhelb97o3u3g1u5o20"
                  method="POST"
                  encType="application/x-www-form-urlencoded"
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="Enter your full name"
                        className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Company Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        required
                        placeholder="Enter your company name"
                        className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        placeholder="Enter your phone number"
                        className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        placeholder="Enter your email"
                        className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="products" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Products Required <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="products"
                        name="products"
                        required
                        placeholder="e.g., Potatoes, Onions, Tomatoes"
                        className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="quantity" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Expected Quantity (kg/week)
                      </label>
                      <input
                        type="text"
                        id="quantity"
                        name="quantity"
                        placeholder="e.g., 500"
                        className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Additional Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about your requirements..."
                      className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    ></textarea>
                    <p className="text-foreground-400 text-xs mt-1">Maximum 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-send-plane-fill text-lg"></i>
                    Request Bulk Supply Quote
                  </button>
                </form>
              </div>
            </div>
          </FadeInSection>
        </section>

        {/* WhatsApp CTA */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-primary-950 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 right-10 w-80 h-80 rounded-full bg-accent-500 blur-3xl"></div>
            <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-primary-400 blur-3xl"></div>
          </div>
          <FadeInSection>
            <div className="max-w-4xl mx-auto text-center relative z-10">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
                Prefer a Quick Chat?
              </h2>
              <p className="text-background-50/60 text-base md:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                Reach out to us directly on WhatsApp for immediate bulk supply inquiries and pricing discussions.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => openEnquiry()}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 hover:bg-accent-400 text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  WhatsApp Now
                </button>
                <a
                  href="tel:+919084756574"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50/10 hover:bg-background-50/20 text-background-50 border border-background-50/20 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-fill text-lg"></i>
                  +91 9084756574
                </a>
              </div>
            </div>
          </FadeInSection>
        </section>
      </main>
      <Footer />
    </div>
  );
}